import {
  recurrencesClient,
  type RecurrenceInput,
} from "@recurrences/recurrencesClient";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

export const recurrencesKeys = {
  /** Prefix for invalidating every recurrences query. */
  all: ["recurrences"] as const,
  list: (sessionKey: string, subject: string | null) =>
    ["recurrences", sessionKey, subject] as const,
  detail: (id: string) => ["recurrence", id] as const,
};

export const useRecurrencesList = (
  sessionKey: string,
  subject: string | null,
) =>
  useSuspenseQuery({
    queryKey: recurrencesKeys.list(sessionKey, subject),
    queryFn: () => recurrencesClient.list(subject ?? undefined),
  });

export const useCreateRecurrence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      about,
    }: {
      input: RecurrenceInput;
      about: string[];
    }) => recurrencesClient.create(input, about),
    // RecurrenceFormModal shows this failure beside its Save button.
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: recurrencesKeys.all }),
  });
};

export const useUpdateRecurrence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecurrenceInput }) =>
      recurrencesClient.update(id, input),
    // RecurrenceFormModal shows this failure beside its Save button.
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: recurrencesKeys.all }),
  });
};

export const usePauseRecurrence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recurrencesClient.pause,
    meta: { action: "pause the recurrence" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: recurrencesKeys.all }),
  });
};

export const useResumeRecurrence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recurrencesClient.resume,
    meta: { action: "resume the recurrence" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: recurrencesKeys.all }),
  });
};

export const useEndRecurrence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recurrencesClient.end,
    meta: { action: "end the recurrence" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: recurrencesKeys.all }),
  });
};

export const useRunRecurrence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recurrencesClient.runNow,
    meta: { action: "run the recurrence now" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: recurrencesKeys.all }),
  });
};
